import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import useStepPost from '../../hooks/useStepPost';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  ACCOUNT_STEP_COMPLETE,
  APPLICATION_TERMS_ACCEPTED,
} from '../../util/gtmEvents';
import useApplicationFundingData from '../../hooks/useApplicationFundingData';
import useBankApplicationFundingData from '../../hooks/useBankApplicationFundingData';
import useBankApplicationFundingAmountDispatch from '../../hooks/useBankApplicationFundingAmountDispatch';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { AGREEMENT_TYPES, EDIT_IRA_FUNDING_TYPES } from '../../util/constants';
import { selectBankAccounts } from '../../store/selectors/bankAccountSelectors';
import { fetchBankAccounts } from '../../store/actions/bankAccount/bankAccountActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import useApplicationFundingAmountDispatch from '../../hooks/useApplicationFundingAmountDispatch';
import Paragraph from '../Paragraph/Paragraph';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import { TAndCLink } from '../ReviewElements';
import Back from '../Buttons/Back';
import { HeadingSubtitle } from '../Heading';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import Checkbox from '../Fields/Checkbox';
import CurrencyInput from '../CurrencyInput';
import useFundingTypeHook from '../../util/helpers/useFundingTypeHook';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import { createAgreement } from '../../store/actions/agreement/agreementActions';
import { formatMoney } from '../../util/helpers/numeralHelpers';
import PrintModal from '../PrintModal/PrintModal';
import AgreementDisplay from '../Agreements/AgreementDisplay';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import themeColors from '../../assets/styles/themeColors';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const TAndC = styled(TAndCLink)`
  color: ${themeColors.colorTextSecondary};
`;

const ChecboxWrapper = styled.div`
  display: flex;
`;

const FundAmountForm = ({
  nextPath,
  onModalClose,
  editFundFlow,
  handleBack,
  skipPreviousStep,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const [bankAccount, setBankAccount] = useState({});
  const [titles, setTitles] = useState({
    title: t('fundAccount.amountTitle'),
    subtitle: t('fundAccount.amountSubtitle'),
  });
  const dispatch = useDispatch();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { gtmEvent } = useGtmHook();
  const { ApplicationFunding } = useApplicationFundingData();
  const { BankApplicationFunding } = useBankApplicationFundingData();
  const { MetalStreamData } = useMetalStreamData();
  const [isACHAgreementOpen, setIsACHAgreementOpen] = useState(false);
  const [errorOccurs, setErrorOccurs] = useState(false);
  const ApplicantData = useApplicantData();
  const { ApplicationUid } = useApplicationUid();
  const {
    applicationFundingAmountDispatch,
  } = useApplicationFundingAmountDispatch();
  const {
    bankApplicationFundingAmountDispatch,
  } = useBankApplicationFundingAmountDispatch();

  const {
    AchDepositLimit,
    CheckDepositLimit,
    CryptocurrencyLimit,
    DisplayBrokerCode,
  } = useSelector(selectSettings);

  const { isCheck, isPlaid, isWire, isCrypto } = useFundingTypeHook({
    ApplicationFunding,
    BankApplicationFunding,
  });

  const bankAccounts = useSelector(selectBankAccounts);
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const gtmStepLabelCopy = () => {
    if (isCheck) {
      return 'Check';
    }
    if (isCrypto) {
      return 'Crypto';
    }
    if (isPlaid) {
      return 'Ach';
    }
    if (isWire) {
      return 'Wire';
    }
  };

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: `Fund your account - ${gtmStepLabelCopy()}`,
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: ['FundAmount', isPlaid && 'AutoDeposit'],
      ...(isWire && {
        maxAmount: 500000000,
      }),
      ...(isCheck && {
        noValidation: true,
      }),
      ...(isPlaid && {
        noValidation: true,
      }),
      ...(isCrypto && {
        noValidation: true,
      }),
    }),
  });

  const { handleSubmit, control, setError, setValue, watch } = useForm({
    resolver: yupResolver(formValidationSchema),
    defaultValues: {
      FundAmount: ApplicationFunding?.Amount || BankApplicationFunding?.Amount,
      AutoDeposit: !!(
        ApplicationFunding?.Amount || BankApplicationFunding?.Amount
      ),
    },
  });

  const onSuccessAPI = () => {
    gtmEvent(APPLICATION_TERMS_ACCEPTED);
    onSuccess();
  };

  const onRequestSuccess = () => {
    if (isPlaid) {
      dispatch(
        createAgreement({
          ApplicationUid,
          type: AGREEMENT_TYPES.ACH,
          onSuccess: onSuccessAPI,
        }),
      );
      handleGtmEvent();
    } else {
      handleGtmEvent();
      onSuccess();
    }
  };

  const onSubmit = ({ FundAmount }) => {
    const onError = (errorMessage) => {
      setError('FundAmount', errorMessage);
    };

    if (isPlaid) {
      bankApplicationFundingAmountDispatch({
        data: {
          Amount: FundAmount,
        },
        onSuccess:
          eventType === 'Form'
            ? () => onRequestSuccess()
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError:
          eventType === 'Form'
            ? () => onError()
            : () => {
                setHasError(true);
                setSaveAndCloseModalOpen(true);
              },
      });
      return;
    }

    applicationFundingAmountDispatch({
      data: {
        Amount: FundAmount,
      },
      onSuccess:
        eventType === 'Form'
          ? () => onRequestSuccess()
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => onError()
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
  };

  useEffect(() => {
    if (isPlaid) {
      dispatch(
        fetchBankAccounts({
          ApplicationUid,
        }),
      );
    }
  }, [ApplicationUid, dispatch, isPlaid]);

  useEffect(() => {
    if (BankApplicationFunding && isPlaid) {
      const { BankAccountUid } = BankApplicationFunding;

      if (BankAccountUid && !isEmpty(bankAccounts)) {
        const selectedBankAccount = bankAccounts.find(
          (bank) => bank.BankAccountUid === BankAccountUid,
        );
        if (selectedBankAccount) {
          setBankAccount(selectedBankAccount);
        }
      }
    }
  }, [
    BankApplicationFunding,
    BankApplicationFunding?.BankAccountUid,
    bankAccounts,
    isPlaid,
  ]);

  useEffect(() => {
    const checkInvestmentType = () => {
      if (isCheck) {
        return 'check';
      }
      if (isWire) {
        return 'wire';
      }
    };

    if (isPlaid) {
      setTitles({
        title: t('fundAccount.plaidTitle'),
        subtitle: t('fundAccount.plaidSubtitle'),
      });
    } else {
      setTitles({
        title: t('fundAccount.amountTitle'),
        subtitle: t('fundAccount.amountSubtitle', {
          type: checkInvestmentType(),
        }),
      });
    }
  }, [isPlaid, isCheck, isWire, t]);

  const limitationCopy = () => {
    if (isCheck) {
      return t('fundAccount.limitationCopyCheck', {
        maxAmount: formatMoney(CheckDepositLimit),
      });
    }
    if (isCrypto) {
      return t('fundAccount.limitationCopyCrypto', {
        maxAmount: formatMoney(CryptocurrencyLimit),
      });
    }
    if (isPlaid) {
      return t('fundAccount.limitationACH', {
        maxAmount: formatMoney(AchDepositLimit),
      });
    }
  };

  const isAutoDepositChecked = watch('AutoDeposit');
  const amountFieldValue = watch('FundAmount');

  const isContinueButtonDisabled = () => {
    if (errorOccurs) {
      return true;
    }
    if (!isPlaid) {
      if (amountFieldValue) {
        return false;
      }

      return true;
    }

    return !(isAutoDepositChecked && amountFieldValue);
  };

  const isInputDisabled = () =>
    !(
      !!ApplicationFunding ||
      !!BankApplicationFunding ||
      !!MetalStreamData ||
      errorOccurs
    );

  const checkValidation = (inputValue) => {
    if (isCheck && inputValue > CheckDepositLimit) {
      setErrorOccurs(true);
      return;
    }

    if (isPlaid && inputValue > AchDepositLimit) {
      setErrorOccurs(true);
      return;
    }

    if (isCrypto && inputValue > CryptocurrencyLimit) {
      setErrorOccurs(true);
      return;
    }

    setErrorOccurs(false);
  };

  return (
    <>
      {editFundFlow && (
        <Back
          onClick={() => {
            if (editFundFlow === EDIT_IRA_FUNDING_TYPES.fundingAmount) {
              skipPreviousStep();
            } else {
              handleBack();
            }
          }}
        />
      )}

      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {titles?.title}
      </HeadingSubtitle>
      <Paragraph mb={32} color={themeColors.colorTextXLLight} fontWeight={400}>
        {titles?.subtitle}
      </Paragraph>
      <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          render={({ field: { onChange, ...field }, fieldState }) => (
            <CurrencyInput
              label={t('fundAccount.amountFieldLabel')}
              {...field}
              {...fieldState}
              autoFocus
              onChange={(e) => {
                onChange(e);
                checkValidation(e.target.value);
              }}
            />
          )}
          name="FundAmount"
        />
        <Paragraph
          color={
            errorOccurs
              ? themeColors.colorTextError
              : themeColors.colorTextPrimary
          }
          variant="normal"
        >
          {limitationCopy()}
        </Paragraph>
        {isPlaid && (
          <FormContainer mt={22}>
            <ChecboxWrapper>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    onClick={() => {
                      onChange(!value);
                    }}
                    value={value}
                  />
                )}
                name="AutoDeposit"
              />
              <Paragraph>
                {t('fundAccount.agreeToAuthorize', {
                  bankName: bankAccount?.BankName,
                  accountNum: bankAccount?.AccountNumber?.substr(
                    (bankAccount?.AccountNumber).length - 4,
                  ),
                  brokerCode: DisplayBrokerCode,
                })}
                <TAndC onClick={() => setIsACHAgreementOpen(true)}>
                  {t('fundAccount.transferTerms')}
                </TAndC>
              </Paragraph>
            </ChecboxWrapper>
          </FormContainer>
        )}
        <OnBoardingCta
          type="submit"
          disabled={isContinueButtonDisabled() || isInputDisabled()}
          onClick={() => setEventType('Form')}
          title={t('continue')}
        />
      </FormWrap>
      <PrintModal
        isOpen={isACHAgreementOpen}
        onClose={() => {
          setIsACHAgreementOpen(false);
          setValue('AutoDeposit', false, {
            shouldDirty: true,
          });
        }}
        onCloseButton={() => {
          setValue('AutoDeposit', true, {
            shouldDirty: true,
          });
          setIsACHAgreementOpen(false);
        }}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
      >
        <AgreementDisplay type={AGREEMENT_TYPES.ACH} />
      </PrintModal>
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={() => onRequestSuccess()}
        />
      )}
    </>
  );
};

FundAmountForm.propTypes = {
  onModalClose: PropTypes.func,
  nextPath: PropTypes.string,
  editFundFlow: PropTypes.string,
  handleBack: PropTypes.func,
  skipPreviousStep: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default FundAmountForm;
